import type { GameId, RoomId } from "@clubap/shared";
import type { ElementKind, Facing } from "@clubap/shared";

/**
 * ==========================================================================
 *  EL ÚNICO ARCHIVO QUE TOCAS CUANDO TIENES ARTE NUEVO.
 * ==========================================================================
 *
 * Todo lo que no esté declarado acá se dibuja con primitivas de Pixi
 * (`art/procedural/`). Así el juego corre completo sin un solo PNG, y cada
 * imagen que agregues reemplaza su fallback sin tocar más código.
 *
 * Especificación de arte: docs/ART.md
 * Exportación desde Blender: docs/BLENDER.md
 */

/**
 * Fondos por sala. Pon un archivo de 960x540 (o 1920x1080 para 2x) en
 * `public/art/rooms/` y descomenta su línea.
 */
export const ROOM_BACKGROUNDS: Partial<Record<RoomId, string>> = {
  taws: "/art/rooms/taws.png",
  /*
   * JPG y no PNG: es una ilustración sin transparencia, y en PNG pesaba 1.5 MB contra
   * 260 KB. Está recortada a 16:9 exacto (1365x768) desde el original de 1376x768, que el
   * motor habría estirado. Las gradas y la mesa de la cancha salen de esta imagen:
   * sus objetos en `rooms.ts` están medidos sobre ella.
   */
  coliseo: "/art/rooms/coliseo.jpg",
  /*
   * JPG y no PNG a propósito: es una ilustración a sangre, sin nada transparente que
   * perder, y en PNG pesaba diez veces más. El original sin recortar vive en
   * `_original/`, como el de la plazoleta.
   *
   * Va a 1920x1071 porque el mundo se autora a 960x540 y Pixi renderiza a
   * `min(devicePixelRatio, 2)`: todo píxel por encima de 1920 de ancho se descarta al
   * dibujar, pero lo descargan igual todos los jugadores. Ojo que
   * `optimize-background.mjs` NO sirve para recortarlo — es sólo PNG.
   */
  fiecvieja: "/art/rooms/fiecvieja.jpg",
  /*
   * JPG, sin procesar todavía: 2752x1536 (1.79:1, casi 16:9 exacto) y 2.1 MB. No hay
   * `optimize-background.mjs` para JPG —ese script sólo lee PNG—, así que falta
   * pasarla por algo que la baje a ~1920 de ancho antes de publicar la sala de verdad.
   * Sirve para trazar el piso con F2 mientras tanto.
   */
  fiecviejasur: "/art/rooms/fiecviejasur.jpg",
  /* JPG, 2000x1116 (1.79:1, casi 16:9 exacto) y 313 KB — ya liviana, sin pasar por
     ningún script. La terraza de Sweet & Coffee, con mesas dibujadas en el fondo. */
  sweetpatio: "/art/rooms/sweetpatio.jpg",
  /* JPG, 2752x1536 (1.79:1) y 1.9 MB, sin procesar — falta bajarla de peso antes de
     publicar. El pasillo/atrio de Bloque 11B, con tres puertas dibujadas al fondo. */
  bloque11b: "/art/rooms/bloque11b.jpg",
  /*
   * JPG, 2750x1536 (1.79:1) y 2.1 MB, sin procesar. El vestíbulo de Bloque 11A, con el
   * banner de ESPOL. La escalera y el techo siguen a escala de edificio, no de cuarto
   * de juego — el avatar no se achica con la profundidad, así que junto a él este
   * espacio se va a sentir más grande de lo que el resto del campus se siente.
   * Pendiente de una pasada de regenerado si se quiere corregir.
   */
  bloque11a: "/art/rooms/bloque11a.jpg",
  /*
   * JPG, 1024x572 (1.79:1) y 109 KB. La salida de Bloque 11A hacia el campus.
   * Regenerada para quitar el desenfoque que tenía el primer plano — ya se puede
   * trazar el piso caminable con F2 mirándola.
   */
  salida11a: "/art/rooms/salida11a.jpg",
  // Las demás salas del campus se dibujan con `art/procedural/`. Cada PNG que
  // agregues acá reemplaza su escenario sin tocar nada más.
  plazoleta: "/art/rooms/plazoleta.png",
  taws2: "/art/rooms/taws2.png",
};

/**
 * El fondo del partido de volley. `null` lo dibuja con primitivas.
 *
 * Va aparte de `ROOM_BACKGROUNDS` porque la cancha no es una sala del campus: tiene su
 * propio lienzo (`COURT`, 640x360) visto de perfil. Se estira a ese lienzo, así que tiene
 * que ser 16:9 y llevar la red en el centro exacto y el borde de la cancha a la altura
 * de `GROUND_Y` (el 89 % del alto): ahí es donde la física pone el piso y la red, y la
 * imagen tiene que coincidir o el balón rebota en el aire.
 *
 * Recortada a 1365x768 desde el original de 1375x768.
 */
export const VOLLEY_COURT_ART: string | null = "/art/volley/cancha.jpg";

/** Una secuencia de fotogramas. Un string suelto es una secuencia de uno. */
export type Frames = string | string[];

/**
 * Fotogramas por dirección.
 *
 * Las direcciones que falten se resuelven espejando su opuesta, y si tampoco
 * existe, cayendo a `s`. Declarar `s se e ne n` alcanza para las ocho.
 */
export type DirectionalFrames = Frames | Partial<Record<Facing, Frames>>;

export interface AvatarLayer {
  /** Pose quieta. Obligatoria: es la referencia de escala y el fallback. */
  idle: DirectionalFrames;
  /**
   * Ciclo de caminata. Si tiene más de un fotograma, el motor lo reproduce y
   * **desactiva** el rebote procedural para no animar dos veces.
   *
   * Si lo omites, se usa `idle` con el rebote por código.
   */
  walk?: DirectionalFrames;
  /**
   * Si es true, esta capa se tiñe con el color del jugador — rendérizala en
   * escala de grises, porque el tinte multiplica.
   *
   * El sombreado en gris **sobrevive** al tinte: un caparazón gris con luces y
   * sombras, teñido de rojo, da un caparazón rojo correctamente sombreado. Por
   * eso conviene teñir el caparazón y no el cuerpo.
   */
  tinted?: boolean;
}

export interface AvatarArt {
  /**
   * Capas de abajo hacia arriba. La primera manda la escala: la altura de su
   * primer fotograma define el tamaño en juego y todas las demás la comparten.
   */
  layers: AvatarLayer[];
}

/**
 * Avatar por sprites. Mientras sea `null` se usa el personaje vectorial.
 *
 * Empieza por una sola dirección para validar escala y anclaje antes de
 * renderizar las 90 imágenes del set completo:
 *
 *   export const AVATAR_ART: AvatarArt | null = {
 *     layers: [
 *       { idle: "/art/avatar/cuerpo-idle-s.png" },
 *       { idle: "/art/avatar/caparazon-idle-s.png", tinted: true },
 *     ],
 *   };
 *
 * Y el set completo, con caminata:
 *
 *   const DIRS = ["s", "se", "e", "ne", "n"] as const;
 *   const walk = (part: string) => Object.fromEntries(
 *     DIRS.map((d) => [d, [0, 1, 2, 3].map((f) => `/art/avatar/${part}-walk-${d}-${f}.png`)]),
 *   );
 *   const idle = (part: string) => Object.fromEntries(
 *     DIRS.map((d) => [d, `/art/avatar/${part}-idle-${d}.png`]),
 *   );
 *
 *   export const AVATAR_ART: AvatarArt | null = {
 *     layers: [
 *       { idle: idle("cuerpo"), walk: walk("cuerpo") },
 *       { idle: idle("caparazon"), walk: walk("caparazon"), tinted: true },
 *     ],
 *   };
 */
const TEST_DIRS = ["s", "se", "e", "ne", "n"] as const;

function testClip(part: string, clip: "idle" | "walk"): DirectionalFrames {
  const frames: Partial<Record<Facing, Frames>> = {};

  for (const dir of TEST_DIRS) {
    frames[dir] =
      clip === "idle"
        ? `/art/avatar/_test/${part}-idle-${dir}.svg`
        : [0, 1, 2, 3].map((frame) => `/art/avatar/_test/${part}-walk-${dir}-${frame}.svg`);
  }

  return frames;
}

/**
 * Avatar de PRUEBA, generado por `node scripts/make-test-avatar.mjs`.
 *
 * Sirve para separar "mi arte está mal" de "el motor está mal". Si al activarlo
 * el personaje gira al caminar, mueve las patas, y cada jugador tiene el
 * caparazón de otro color con el sombreado intacto, entonces el motor funciona.
 */
export const TEST_AVATAR: AvatarArt = {
  layers: [
    { idle: testClip("cuerpo", "idle"), walk: testClip("cuerpo", "walk") },
    { idle: testClip("caparazon", "idle"), walk: testClip("caparazon", "walk"), tinted: true },
  ],
};

/**
 * La Campus Turtle.
 *
 * Una sola capa y sin tinte: el caparazón viene integrado al cuerpo en el render,
 * así que no se puede teñir por separado. Todos los jugadores se ven igual por
 * ahora y se distinguen por el nombre sobre la cabeza.
 *
 * SET COMPLETO: las ocho direcciones con cinco archivos.
 *
 * Las tres que no están declaradas las resuelve el motor espejando su opuesta:
 *   sw = se espejada    e = w espejada    ne = nw espejada
 *
 * `n` y `s` no se espejan porque son simétricas de frente, así que ésas sí
 * tienen que existir.
 *
 * Cada archivo mira hacia donde le toca —`se` a la derecha, `w` y `nw` a la
 * izquierda— y cada dirección se espeja por separado, así que la mezcla de
 * costados entre archivos es correcta.
 *
 * Para volver al set de prueba: cambia esto por TEST_AVATAR.
 */
export const AVATAR_ART: AvatarArt | null = {
  layers: [
    {
      idle: {
        s: "/art/avatar/s.png",
        se: "/art/avatar/se.png",
        w: "/art/avatar/w.png",
        nw: "/art/avatar/nw.png",
        n: "/art/avatar/n.png",
      },
    },
  ],
};

/**
 * Arte por ítem: una capa que se dibuja sobre el cuerpo cuando el jugador lo lleva
 * puesto. Mismas reglas que el avatar —mismo lienzo, misma altura de personaje—
 * porque comparten la escala.
 *
 * El DATO se mudó a `itemArt.ts`, que lo reescribe el configurador de ítems. Acá
 * queda el reexporte para que este archivo siga siendo la puerta del arte: si buscas
 * arte, entras por aquí y desde aquí se ve a dónde ir.
 *
 * Lo que ganó al mudarse es la COLOCACIÓN. Antes un ítem no tenía posición —era una
 * capa sobre el mismo lienzo y la alineación estaba horneada en el archivo—, así que
 * subir una gorra dos píxeles era reexportar el dibujo. Ahora es un número.
 */
export { ITEM_ART, NEUTRAL_PLACEMENT, placementFor } from "./itemArt.js";
export type { ItemArt, ItemPlacement } from "./itemArt.js";

/**
 * Arte de los objetos del mapa, por tipo.
 *
 * Pon el archivo en `public/art/props/` y descomenta su línea. Se autora sobre el
 * mismo lienzo de 960x540 que los fondos, así que las coordenadas del catálogo y las
 * del dibujo son las mismas y el objeto cae donde lo colocaste en el editor.
 *
 * Se indexa por CLAVE DE ARTE, no por tipo: puede haber muchas sillas y muchas mesas
 * y no tienen por qué verse iguales. El tipo carga el COMPORTAMIENTO — dos sillas de
 * distinto color se sientan igual — así que la variedad visual va por otro eje.
 *
 * Cada instancia del catálogo elige la suya con `art: "silla-roja"`. Sin declararla,
 * usa la clave que coincide con su tipo. Es el mismo trato que ya tienen las salas
 * con `RoomDef.art`.
 *
 * Una clave sin arte se dibuja con la silueta a mano alzada, así que el juego nunca
 * se queda sin nada que mostrar.
 */
export const ELEMENT_ART: Record<string, string> = {
  // Una carpeta por tipo, y varios archivos por carpeta.
  //
  // La clave por defecto es el nombre del tipo: un objeto que no declara `art` usa
  // ésa. Las demás claves son variantes que la instancia pide por su nombre en el
  // catálogo — `art: "silla-roja"`.
  //
  silla: "/art/props/silla/sillarosa.png",
  mesa: "/art/props/mesa/taws-mesa.png",
  /*
   * El mesón de laboratorio con tres PCs. Es una VARIANTE, no el arte por defecto del
   * tipo: se pide desde el catálogo con `art: "mesa-pcs"`, así que una mesa cualquiera
   * del campus sigue siendo una mesa cualquiera.
   *
   * Su dibujo va en perspectiva —del fondo al frente, bajando hacia la derecha— así
   * que el `rect` que la use tiene que ir contra una pared que corra en ese sentido, y
   * con proporción parecida a 1.19 (el ancho del SVG entre su alto). El motor estira
   * el sprite al rect, así que una caja de otra forma la deforma.
   */
  "mesa-pcs": "/art/props/mesa/mesaspcs.svg",
  /*
   * Otra mesa del TAWS. Es una VARIANTE: se pide desde el catálogo con
   * `art: "mesa-taws"`, así que una mesa cualquiera sigue usando la clave `mesa`.
   */
  "mesa-taws": "/art/props/mesa/mesa-taws.svg",
  /*
   * El balón del coliseo: tocarlo abre el partido de volley. Es la VARIANTE que pide la
   * cancha (`art: "balon-volley"`), no la clave de su tipo, porque "cancha" nombra lo que
   * el objeto hace y el balón es sólo cómo se ve.
   */
  "balon-volley": "/art/props/balon/balon-volley.svg",
  /*
   * El carrito de la FIEC Vieja. Es una VARIANTE y no el arte por defecto del tipo
   * `juego`: un carrito no es lo que parece cualquier puesto de juego del campus, así
   * que se pide desde el catálogo con `art: "carrito"`.
   *
   * Proporción 1.7936 (669x373). El motor estira el sprite al rect, así que una caja
   * de otra forma lo deforma y `check:map` lo avisa.
   */
  carrito: "/art/props/nat_carrito/carrito.png",
  // "silla-roja": "/art/props/silla/roja.png",
  // "mesa-redonda": "/art/props/mesa/redonda.png",
  arbol: "/art/props/arbol/arbol.png",
};

/**
 * Arte de los MINIJUEGOS, por juego y por pieza.
 *
 * Va aparte de todo lo demás porque no comparte nada con lo demás: el arte del campus
 * se autora sobre el lienzo de 960x540 y se coloca en coordenadas del mundo, y un
 * minijuego tiene su propio lienzo y sus propias medidas. Meterlos en el mismo mapa
 * dejaría a `ELEMENT_ART` con entradas que nada del mapa puede usar.
 *
 * La clave de adentro la decide cada juego. El Frozen pide una por cosa que cae —las
 * mismas de `FROZEN_KINDS`— más el vaso y el salpicón; el avatar NO está acá porque lo
 * pone el jugador: el juego reusa `AVATAR_ART` y `ITEM_ART` para que saltes con tu
 * color y con lo que lleves puesto.
 *
 * Una pieza que falte se dibuja con una primitiva y el juego sigue andando, así que
 * cambiar cualquiera de estos SVG por un PNG ilustrado es cambiar su ruta y nada más.
 */
export const GAME_ART: Partial<Record<GameId, Record<string, string>>> = {
  frozen: {
    guineo: "/art/games/frozen/guineo.svg",
    frutilla: "/art/games/frozen/frutilla.svg",
    mora: "/art/games/frozen/mora.svg",
    naranjilla: "/art/games/frozen/naranjilla.svg",
    maracuya: "/art/games/frozen/maracuya.svg",
    aji: "/art/games/frozen/aji.svg",
    vaso: "/art/games/frozen/vaso.svg",
    salpicon: "/art/games/frozen/salpicon.svg",
  },
};

/**
 * Retrato de cuerpo entero para la tarjeta de perfil (clic sobre un jugador).
 * Acá va el render detallado: se muestra grande, así que aguanta el detalle que
 * el sprite del mundo no.
 */
export const AVATAR_PORTRAIT: string | null = null;
// export const AVATAR_PORTRAIT: string | null = "/art/avatar/retrato.png";
